/**
 * Payout Service
 *
 * API calls per il modulo payout (v3 accounting).
 */
import ApiClient from "@/domain/core/utils/apiClient"
import client_core from "@/services/clients/client_core"

const client = new ApiClient(client_core)

const endpoints = {
    // Discovery
    scopes_payout: '/dashboard/api/v3/accounting/scopes/payout/',
    scopes_certification: '/dashboard/api/v3/accounting/scopes/certification/',

    // Contract Manager scope - Certification
    cm_validate_vat: '/dashboard/api/v3/accounting/contract-manager/{cmId}/certification/validate-vat/',

    // Contract Manager scope - Payouts
    cm_payouts: '/dashboard/api/v3/accounting/contract-manager/{cmId}/payouts/',
    cm_payout_detail: '/dashboard/api/v3/accounting/contract-manager/{cmId}/payouts/{id}/',
    cm_statistics: '/dashboard/api/v3/accounting/contract-manager/{cmId}/payouts/statistics/',
    cm_statistics_download: '/dashboard/api/v3/accounting/contract-manager/{cmId}/payouts/statistics/download/',
    cm_download: '/dashboard/api/v3/accounting/contract-manager/{cmId}/payouts/{id}/download/',
    cm_upload_invoice: '/dashboard/api/v3/accounting/contract-manager/{cmId}/payouts/{id}/upload-invoice/',
    cm_certification: '/dashboard/api/v3/accounting/contract-manager/{cmId}/certification/',
    cm_certification_submit: '/dashboard/api/v3/accounting/contract-manager/{cmId}/certification/submit/',
    cm_certification_iban: '/dashboard/api/v3/accounting/contract-manager/{cmId}/certification/update-iban/',
}

/**
 * Sostituisce i placeholder nell'URL
 */
const buildUrl = (endpoint, replacements = {}) => {
    let url = endpoint
    Object.entries(replacements).forEach(([key, value]) => {
        url = url.replace(`{${key}}`, value)
    })
    return url
}

export default {
    // ==================== Discovery ====================

    /**
     * Fetch scopes accessibili per l'utente (contract_managers e pica_services)
     * @returns {Promise<{contract_managers: Array, pica_services: Array}>}
     */
    async fetchScopes() {
        return await client.get(endpoints.scopes_payout)
    },

    async fetchCertificationScopes() {
        return await client.get(endpoints.scopes_certification)
    },

    // ==================== Contract Manager Scope ====================

    /**
     * Fetch lista payouts per un contract manager
     * @param {number} cmId - Contract Manager ID
     * @param {Object} filters - Filtri (contract_id, status, section)
     * @param {Object} pagination - { page, page_size }
     * @param {Object} sorting - { column, asc }
     */
    async fetchPayoutList(cmId, filters = {}, pagination = {}, sorting = {}) {
        const url = buildUrl(endpoints.cm_payouts, { cmId })
        const params = {
            ...filters,
            page_size: pagination.page_size || 20,
            page: pagination.page || 1,
        }

        if (sorting.column) {
            params.ordering = sorting.asc ? sorting.column : `-${sorting.column}`
        }

        const result = await client.get(url, params)

        return {
            results: result.results || [],
            count: result.count || 0,
            pagination: {
                num_pages: Math.ceil((result.count || 0) / (pagination.page_size || 20)),
                page_size: pagination.page_size || 20,
                page: pagination.page || 1,
                total: result.count || 0,
            }
        }
    },

    /**
     * Fetch dettaglio payout
     * @param {number} cmId - Contract Manager ID
     * @param {number} payoutId - Payout ID
     */
    async fetchPayoutDetail(cmId, payoutId) {
        const url = buildUrl(endpoints.cm_payout_detail, { cmId, id: payoutId })
        return await client.get(url)
    },

    /**
     * Fetch statistiche aggregate per contract manager
     * @param {number} cmId - Contract Manager ID
     * @param {number|null} contractId - Filtra per contratto (opzionale)
     */
    async fetchStatistics(cmId, contractId = null) {
        const url = buildUrl(endpoints.cm_statistics, { cmId })
        const params = contractId ? { contract_id: contractId } : {}
        return await client.get(url, params)
    },

    /**
     * Download excel statistiche mese corrente
     * @param {number} cmId - Contract Manager ID
     */
    async downloadStatistics(cmId) {
        const url = buildUrl(endpoints.cm_statistics_download, { cmId })
        await client.download(url)
    },

    /**
     * Download excel payout
     * @param {number} cmId - Contract Manager ID
     * @param {number} payoutId - Payout ID
     */
    async downloadPayout(cmId, payoutId) {
        const url = buildUrl(endpoints.cm_download, { cmId, id: payoutId })
        await client.download(url)
    },


    /**
     * Upload fattura per un payout
     * @param {number} cmId - Contract Manager ID
     * @param {number} payoutId - Payout ID
     * @param {File} invoice - File fattura
     */
    async uploadInvoice(cmId, payoutId, invoice) {
        const url = buildUrl(endpoints.cm_upload_invoice, { cmId, id: payoutId })

        const formData = new FormData()
        formData.append('invoice', invoice)

        return await client.patch(url, formData)
    },

    /**
     * Verifica P.IVA tramite VIES (solo EU)
     * @param {number} cmId - Contract Manager ID
     * @param {string} vatNumber - Partita IVA da verificare
     * @returns {Promise<{is_valid: boolean}>}
     */
    async validateVat(cmId, vatNumber) {
        const url = buildUrl(endpoints.cm_validate_vat, { cmId })
        return await client.post(url, { vat_number: vatNumber })
    },

    /**
     * Fetch stato certificazione azienda
     * @param {number} cmId - Contract Manager ID
     */
    async fetchCertification(cmId) {
        const url = buildUrl(endpoints.cm_certification, { cmId })
        return await client.get(url)
    },

    /**
     * Invia certificazione azienda
     * @param {number} cmId - Contract Manager ID
     * @param {FormData} data - Dati certificazione
     */
    async submitCertification(cmId, data) {
        const url = buildUrl(endpoints.cm_certification, { cmId })
        return await client.post(url, data)
    },

    /**
     * Aggiorna certificazione azienda
     * @param {number} cmId - Contract Manager ID
     * @param {FormData} data - Dati certificazione aggiornati
     */
    async updateCertification(cmId, data) {
        const url = buildUrl(endpoints.cm_certification_submit, { cmId })
        return await client.patch(url, data)
    },

    /**
     * Aggiorna solo IBAN per certificazione già approvata
     * @param {number} cmId - Contract Manager ID
     * @param {string} iban - Nuovo IBAN
     */
    async updateCertificationIban(cmId, iban) {
        const url = buildUrl(endpoints.cm_certification_iban, { cmId })
        return await client.patch(url, { iban })
    },
}
